import { useEffect, useRef, useState } from 'react';
import { ApiRequestError } from './client';

export type ApiState<T> =
  | { status: 'loading' }
  | { status: 'error'; error: ApiRequestError | Error }
  | { status: 'empty' }
  | { status: 'ready'; data: T };

/** Fetches on mount (and whenever `deps` changes), and maps the result
 * into a loading/error/empty/ready union so every page/component
 * renders the same four states consistently (task requirement #13),
 * instead of each page re-inventing its own ad hoc loading flag. */
export function useApiData<T>(fetcher: () => Promise<T>, deps: React.DependencyList, isEmpty: (data: T) => boolean = (d) => Array.isArray(d) && d.length === 0): ApiState<T> {
  const [state, setState] = useState<ApiState<T>>({ status: 'loading' });
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetcherRef
      .current()
      .then((data) => {
        if (cancelled) return;
        setState(isEmpty(data) ? { status: 'empty' } : { status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

export function isNotFound(error: Error): boolean {
  return error instanceof ApiRequestError && error.status === 404;
}
